import { runInAction, decorate, observable, action } from "mobx";
import { getFilteredGatewaySMS, getGatewaySmsStats, getBulkListForApproval, approveBulk, getQuickMessagePendingList } from "service/gatewayService";
import { getGatewayMmsStats, getFilteredGatewayMMS } from "service/gatewayMmsService";
import { getAllNonActiveUsers, deleteNonActiveUser } from "service/userService";
import {
  getTotalSystemUsage,
  getUsersExpense,
  getTotalSystemPurchase,
  getProviderRoutes,
  deleteProviderRoutes,
  mapProviderRoute,
  updateProviderMapping,
  getProviderSQS,
  deleteProviderSQS,
  mapProviderSQS,
  updateProviderSQS,
  getAllProviders,
  getDefaultProvider,
  setDefaultProvider,
  getUserPurchaseOrders,
  fetchDedicatedNumbers,
  getUserInvoices,
  getSubAccountHistory
} from "service/adminService";
import moment from "moment";
import {
  activateDedicatedNum,
  addBulkDedicatedNums,
  addDedicatedNumber,
  dedicatedNumHistory,
  deleteDedicatedNumber,
  editDedicatedNumber,
  getAllDedicatedNo,
  getNumberPrice,
  releaseDedicationNumber
} from "service/dedicatedNumber";
import { getVerificationForms, verificationFormAction } from "service/userVerification";
import { message } from "antd";

class ObservableAdminGatewayStore {
  adminDashboardGatewaySMSList = [];
  adminDashboardGatewayMMSList = [];
  adminDashboardSmsStats = undefined;
  adminDashboardMmsStats = undefined;
  adminBulkListForApproval = [];
  bulkApprovalResponse = undefined;
  selectedBulk = undefined;
  selectedDedicatedNum = undefined;
  userDeleteResponse = undefined;
  dedicatedNumResponse = undefined;
  providerRouteResponse = undefined;
  usersList = [];
  total = 0;
  pageSize = 10;
  current = 1;
  search = "";
  statSearch = "";
  sortField = "createdAt";
  sortOrder = "descend";
  status = "";
  type = "sms";
  activeNum = "";
  numStatus = "";
  numType = "";
  numHistory = [];
  verificationFormList = [];
  verificationResponse = undefined;

  providerList = [];
  providerSQSList = [];
  defaultProvider = undefined;

  totalUsageData = undefined;
  totalPurchaseData = undefined;
  userTotalPurchaseData = undefined;
  totalUserExpenseData = undefined;
  selectedUserId = null;

  // Add separate pagination states for each table
  userPurchaseOrdersList = [];
  userPurchaseOrdersResponse = undefined;
  purchaseOrdersDateRange = {
    from: null,
    to: null
  };
  userEmailFilter = "";
  mediumFilter = null;

  // Pagination for Purchase Orders
  purchaseOrdersPagination = {
    total: 0,
    pageSize: 10,
    current: 1,
    search: "",
    sortField: "createdAt",
    sortOrder: "descend"
  };

  userSenderIdsUsageList = [];
  userSenderIdsList = [];
  userSenderIdsResponse = undefined;
  userSenderIdsFilters = {
    userEmail: "",
    from: "",
    to: "",
    phone: "",
    provider: ""
  };

  // Pagination for Sender IDs (Virtual Numbers)
  senderIdsPagination = {
    total: 0,
    pageSize: 10,
    current: 1,
    search: "",
    sortField: "createdAt",
    sortOrder: "descend"
  };

  // Pagination for subaccount page
  subAccountPage = 1;
  subAccountLimit = 10;
  subAccountMonth = null;
  subAccountYear = null;
  subAccountHistory = [];

  // Purchase Orders pagination methods
  setPurchaseOrdersPage = (current, additionalParams = {}) => {
    runInAction(() => {
      this.purchaseOrdersPagination.current = current;
    });

    this.getUserPurchaseOrders(additionalParams);
  };

  setPurchaseOrdersPageSize = (pageSize, additionalParams = {}) => {
    runInAction(() => {
      this.purchaseOrdersPagination.pageSize = pageSize;
      this.purchaseOrdersPagination.current = 1;
    });

    this.getUserPurchaseOrders(additionalParams);
  };

  setPurchaseOrdersSearch = search => {
    runInAction(() => {
      this.purchaseOrdersPagination.search = search;
      this.purchaseOrdersPagination.current = 1;
    });
    this.getUserPurchaseOrders();
  };

  // Sender IDs pagination methods
  setSenderIdsPage = (current, additionalParams = {}) => {
    runInAction(() => {
      this.senderIdsPagination.current = current;
    });

    this.getUserSenderIds(additionalParams);
  };

  setSenderIdsPageSize = (pageSize, additionalParams = {}) => {
    runInAction(() => {
      this.senderIdsPagination.pageSize = pageSize;
      this.senderIdsPagination.current = 1;
    });

    this.getUserSenderIds(additionalParams);
  };

  setSenderIdsSearch = search => {
    runInAction(() => {
      this.senderIdsPagination.search = search;
      this.senderIdsPagination.current = 1;
    });
    this.getUserSenderIds();
  };

  setPurchaseOrdersDateRange = (from, to) => {
    runInAction(() => {
      this.purchaseOrdersDateRange.from = from;
      this.purchaseOrdersDateRange.to = to;
      this.purchaseOrdersPagination.current = 1;
    });
    this.getUserPurchaseOrders();
  };

  setUserEmailFilter = email => {
    runInAction(() => {
      this.userEmailFilter = email;
      this.purchaseOrdersPagination.current = 1;
    });
    this.getUserPurchaseOrders();
  };

  setMediumFilter = medium => {
    runInAction(() => {
      this.mediumFilter = medium;
      this.purchaseOrdersPagination.current = 1;
    });
    this.getUserPurchaseOrders();
  };

  getUserPurchaseOrders = async (args = {}) => {
    const params = {
      page: this.purchaseOrdersPagination.current,
      limit: this.purchaseOrdersPagination.pageSize,
      // medium: this.mediumFilter,
      ...args
    };

    if (this.mediumFilter) {
      params.medium = this.mediumFilter;
    }

    if (this.purchaseOrdersDateRange.from) {
      params.from = this.purchaseOrdersDateRange.from;
    }
    if (this.purchaseOrdersDateRange.to) {
      params.to = this.purchaseOrdersDateRange.to;
    }

    if (this.userEmailFilter) {
      params.userEmail = this.userEmailFilter;
    }

    if (this.purchaseOrdersPagination.search) {
      params.search = this.purchaseOrdersPagination.search;
    }

    try {
      const getUserPurchaseOrdersResponse = await getUserPurchaseOrders(params);
      runInAction(() => {
        const { total, current, limit: pageSize } = getUserPurchaseOrdersResponse.data.pagination;
        this.purchaseOrdersPagination.pageSize = pageSize || this.purchaseOrdersPagination.pageSize;
        this.purchaseOrdersPagination.current = current || this.purchaseOrdersPagination.current;
        this.purchaseOrdersPagination.total = total || 0;
        this.userPurchaseOrdersResponse = getUserPurchaseOrdersResponse;
        this.userPurchaseOrdersList = getUserPurchaseOrdersResponse.data.orders;
      });
    } catch (error) {
      runInAction(() => {
        this.userPurchaseOrdersList = [];
        this.userPurchaseOrdersResponse = error;
      });
    }

    return this.userPurchaseOrdersList;
  };

  setUserSenderIdsFilters = filters => {
    runInAction(() => {
      this.userSenderIdsFilters = { ...this.userSenderIdsFilters, ...filters };
      this.senderIdsPagination.current = 1;
    });
  };

  clearUserSenderIdsFilters = () => {
    runInAction(() => {
      this.userSenderIdsFilters = {
        userEmail: "",
        from: "",
        to: "",
        phone: "",
        provider: ""
      };
      this.senderIdsPagination.current = 1;
    });
  };

  getUserSenderIds = async (additionalParams = {}) => {
    // Store the current parameters for future pagination calls
    this.currentSenderIdsParams = { ...additionalParams };

    const params = {
      page: this.senderIdsPagination.current,
      limit: this.senderIdsPagination.pageSize,
      sortField: this.senderIdsPagination.sortField,
      sortOrder: this.senderIdsPagination.sortOrder,
      ...additionalParams // This will include userId, type, month, year etc.
    };

    // Apply user sender IDs filters
    Object.keys(this.userSenderIdsFilters).forEach(key => {
      if (this.userSenderIdsFilters[key]) {
        params[key] = this.userSenderIdsFilters[key];
      }
    });

    // Apply search if exists
    if (this.senderIdsPagination.search) {
      params.search = this.senderIdsPagination.search;
    }

    try {
      const response = await fetchDedicatedNumbers(params);

      runInAction(() => {
        if (response && response.data) {
          const { total, current, limit: pageSize } = response.data.pagination;
          this.senderIdsPagination.pageSize = pageSize;
          this.senderIdsPagination.current = current;
          this.senderIdsPagination.total = total;
          this.userSenderIdsList = response.data.orders;
          this.userSenderIdsUsageList = response.data.senderIds;
          this.userSenderIdsResponse = response;
        } else {
          this.userSenderIdsList = response || [];
          this.userSenderIdsResponse = response;
        }
      });
    } catch (error) {
      runInAction(() => {
        this.userSenderIdsList = [];
        this.userSenderIdsResponse = error;
        this.status = "error";
      });

      message.error("Failed to fetch user sender IDs");
    }

    return this.userSenderIdsList;
  };

  changeSorting = fieldObj => {
    runInAction(() => {
      const { field, sortOrder } = fieldObj;
      this.sortField = field;
      this.sortOrder = sortOrder;
    });
  };

  setSearch = search => {
    runInAction(() => {
      this.search = search;
      this.current = 1;
    });
  };

  setStatSearch = search => {
    runInAction(() => {
      this.statSearch = search;
    });
  };

  setPage = current => {
    runInAction(() => {
      this.purchaseOrdersPagination.current = current;
      this.current = current;
    });
    // this.getUserPurchaseOrders();
    // this.getUserSenderIds();
  };

  setPageSize = pageSize => {
    runInAction(() => {
      this.pageSize = pageSize;
      this.purchaseOrdersPagination.pageSize = pageSize; // Fix: use purchaseOrdersPagination
      this.purchaseOrdersPagination.current = 1;
    });
    // this.getUserPurchaseOrders();
    // this.getUserSenderIds();
  };

  setMessageType = type => {
    runInAction(() => {
      this.type = type;
      this.current = 1;
    });
  };

  fetchSubAccountHistory = async (userId, options = {}) => {
    if (options.page !== undefined) this.subAccountPage = options.page;
    if (options.limit !== undefined) this.subAccountLimit = options.limit;
    if (options.month !== undefined) this.subAccountMonth = options.month;
    if (options.year !== undefined) this.subAccountYear = options.year;

    try {
      const response = await getSubAccountHistory(userId, {
        page: this.subAccountPage,
        limit: this.subAccountLimit,
        month: this.subAccountMonth,
        year: this.subAccountYear
      });

      runInAction(() => {
        this.subAccountHistory = response.data || [];
      });
    } catch (error) {
      runInAction(() => {
        this.subAccountHistory = [];
        console.error("Something went wrong while getting subaccount");
      });
    }

    return this.subAccountHistory;
  };

  /**
   * get all Gateway SMS
   * @returns {Promise<[]>}
   */
  getSMSGatewayMessages = async args => {
    const params = {
      page: this.current,
      sortField: this.sortField,
      sortOrder: this.sortOrder,
      pageSize: this.pageSize,
      ...(args && args)
    };

    if (this.search) {
      for (var key of Object.keys(this.search)) {
        if (this.search[key]) {
          params[key] = this.search[key];
        }
      }
    }
    const getAllGatewaySMSResponse = await getFilteredGatewaySMS(params);
    try {
      runInAction(() => {
        const { items, total, current, limit: pageSize } = getAllGatewaySMSResponse;
        this.adminDashboardGatewaySMSList = items;
        this.pageSize = pageSize;
        this.current = current;
        this.total = total;
      });
    } catch (error) {
      runInAction(() => {
        this.status = "error";
      });
    }

    return this.adminDashboardGatewaySMSList;
  };
  /**
   * get all Gateway MMS
   * @returns {Promise<[]>}
   */
  getMMSGatewayMessages = async args => {
    const params = {
      page: this.current,
      sortField: this.sortField,
      sortOrder: this.sortOrder,
      pageSize: this.pageSize,
      ...(args && args)
    };

    if (this.search) {
      for (var key of Object.keys(this.search)) {
        if (this.search[key]) {
          params[key] = this.search[key];
        }
      }
    }
    const getAllGatewayMMSResponse = await getFilteredGatewayMMS(params);
    try {
      runInAction(() => {
        const { items, total, current, limit: pageSize } = getAllGatewayMMSResponse;
        this.adminDashboardGatewayMMSList = items;
        this.pageSize = pageSize;
        this.current = current;
        this.total = total;
      });
    } catch (error) {
      runInAction(() => {
        this.status = "error";
      });
    }

    return this.adminDashboardGatewayMMSList;
  };

  /**
   * get all Gateway SMS STATS
   * @returns {Promise<[]>}
   */
  getGatewaySMSStats = async () => {
    const params = {
      search: this.statSearch
    };
    const getGatewayMessageStatsResponse = await getGatewaySmsStats(params);
    try {
      runInAction(() => {
        this.adminDashboardSmsStats = getGatewayMessageStatsResponse;
      });
    } catch (error) {
      runInAction(() => {
        this.status = "error";
      });
    }
    return this.adminDashboardSmsStats;
  };

  /**
   * get all Gateway MMS STATS
   * @returns {Promise<[]>}
   */
  getGatewayMMSStats = async () => {
    const params = {
      search: this.statSearch
    };
    const getGatewayMessageStatsResponse = await getGatewayMmsStats(params);
    try {
      runInAction(() => {
        this.adminDashboardMmsStats = getGatewayMessageStatsResponse;
      });
    } catch (error) {
      runInAction(() => {
        this.status = "error";
      });
    }
    return this.adminDashboardMmsStats;
  };

  selectBulk = bulkData => {
    this.selectedBulk = bulkData ? JSON.parse(JSON.stringify(bulkData)) : undefined;
  };

  /**
   * get all bulk list for approval
   * @returns {Promise<[]>}
   * @param {string} search
   */
  getBulkListForApproval = async () => {
    const params = {
      page: this.current,
      sortField: this.sortField,
      sortOrder: this.sortOrder,
      limit: this.pageSize,
      camp_type: this.type
    };
    if (this.search) params.search = this.search;
    const getBulkListForApprovalResponse = await getBulkListForApproval(params);
    try {
      runInAction(() => {
        const { items, total, current, limit: pageSize } = getBulkListForApprovalResponse;
        this.adminBulkListForApproval = items;
        this.pageSize = pageSize;
        this.current = current;
        this.total = total;
      });
    } catch (error) {
      runInAction(() => {
        this.adminBulkListForApproval = error;
      });
    }
    return this.adminBulkListForApproval;
  };

  /**
   * get quickmessage approval list
   */
  getQuickMessagePendingList = async () => {
    const params = {
      page: this.current,
      sortField: this.sortField,
      sortOrder: this.sortOrder,
      limit: this.pageSize
    };
    if (this.search) params.search = this.search;
    const getMessageListResponse = await getQuickMessagePendingList(params);
    try {
      runInAction(() => {
        const { items, total, current, limit: pageSize } = getMessageListResponse.data;
        this.adminBulkListForApproval = items;
        this.pageSize = pageSize;
        this.current = current;
        this.total = total;
      });
    } catch (error) {
      runInAction(() => {
        this.adminBulkListForApproval = error;
      });
    }
    return this.adminBulkListForApproval;
  };

  /**
   * approve/reject bulk
   * @returns {Promise<[]>}
   * @param {string} id
   * @param {string} status
   */
  approveBulk = async (id, stats) => {
    const approveBulkResponse = await approveBulk(id, stats);
    try {
      runInAction(() => {
        this.bulkApprovalResponse = approveBulkResponse;
        if (!["quicksms", "quickmms"].includes(stats.camp_type)) {
          this.getBulkListForApproval();
        } else {
          this.getQuickMessagePendingList();
        }
      });
    } catch (error) {
      runInAction(() => {
        this.bulkApprovalResponse = error;
      });
    }
    return this.bulkApprovalResponse;
  };

  /**
   * get all non active users
   * @returns {Promise<[]>}
   * @param {string} params
   */
  getAllNonActiveUsers = async () => {
    const params = {
      page: this.current,
      sortField: this.sortField,
      sortOrder: this.sortOrder,
      limit: this.pageSize
    };
    if (this.search) params.search = this.search;
    const usersResponse = await getAllNonActiveUsers(params);
    try {
      runInAction(() => {
        const { items, total, current, limit: pageSize } = usersResponse;
        this.usersList = items;
        this.pageSize = pageSize;
        this.current = current;
        this.total = total;
      });
    } catch (error) {
      runInAction(() => {
        this.usersList = error;
      });
    }
    return this.usersList;
  };

  /**
   * delete non active user
   * @returns {Promise<[]>}
   * @param {string} params
   */
  deleteNonActiveUser = async id => {
    const deleteResponse = await deleteNonActiveUser(id);
    try {
      runInAction(() => {
        this.userDeleteResponse = deleteResponse;
        this.getAllNonActiveUsers();
      });
    } catch (error) {
      runInAction(() => {
        this.userDeleteResponse = error;
      });
    }
    return this.userDeleteResponse;
  };

  /**
   * get total system purchase
   * @param {string} month , year
   * @returns {Promise<[]>}
   */
  getTotalSystemPurchase = async (args, id) => {
    let month = args.month || moment().month();
    let year = args.year || moment().year();
    let params = {
      month,
      year
    };
    if (id) params.userId = id;
    const purchaseResponse = await getTotalSystemPurchase(params);
    try {
      runInAction(() => {
        if (args.userId) {
          this.userTotalPurchaseData = purchaseResponse;
        } else {
          this.totalPurchaseData = purchaseResponse;
        }
      });
    } catch (error) {
      runInAction(() => {
        this.totalPurchaseData = error;
      });
    }
    return this.totalPurchaseData;
  };

  /**
   * get total system usage
   * @param {string} month , year
   * @returns {Promise<[]>}
   */
  getTotalSystemUsage = async (args, id) => {
    let month = args.month || moment().month() + 1;
    let year = args.year || moment().year();
    let params = {
      month,
      year
    };
    if (id) params.userId = id;
    const usageResponse = await getTotalSystemUsage(params);
    try {
      runInAction(() => {
        this.totalUsageData = usageResponse;
      });
    } catch (error) {
      runInAction(() => {
        this.totalUsageData = error;
      });
    }
    return this.totalUsageData;
  };

  /**
   * get total user expense
   * @returns {Promise<[]>}
   * @param {string} userId
   * @param {string} month
   * @param {string} year
   */
  getTotalUserExpense = async args => {
    let month = args.month || moment().month() + 1;
    let year = args.year || moment().year();
    let params = {
      month,
      year,
      ...(args && args)
    };
    const expenseResponse = await getUsersExpense(params);
    try {
      runInAction(() => {
        this.totalUserExpenseData = expenseResponse;
      });
    } catch (error) {
      runInAction(() => {
        this.totalUserExpenseData = error;
      });
    }
    return this.totalUserExpenseData;
  };

  selectUserDetails = id => {
    runInAction(() => {
      this.selectedUserId = id;
    });
  };

  //********** For Dedicated Numbers page *********//

  setActiveNum = active => {
    runInAction(() => {
      this.activeNum = active;
      this.current = 1;
    });
  };

  setNumType = type => {
    runInAction(() => {
      this.numType = type;
      this.current = 1;
    });
  };

  setStatus = status => {
    runInAction(() => {
      this.numStatus = status;
      this.current = 1;
    });
  };

  selectDedicatedNum = paramData => {
    this.selectedDedicatedNum = paramData ? JSON.parse(JSON.stringify(paramData)) : undefined;
  };

  /**
   * get all dedicated number list
   * @returns {Promise<[]>}
   * @param {string} params
   */
  getAllDedicatedNo = async () => {
    const params = {
      page: this.current,
      sortField: this.sortField,
      sortOrder: this.sortOrder,
      limit: this.pageSize,
      status: this.numStatus,
      active: this.activeNum,
      type: this.numType
    };
    if (this.search) params.search = this.search;
    const usersResponse = await getAllDedicatedNo(params);
    try {
      runInAction(() => {
        const { items, total, current, limit: pageSize } = usersResponse;
        this.usersList = items;
        this.pageSize = pageSize;
        this.current = current;
        this.total = total;
      });
    } catch (error) {
      runInAction(() => {
        this.usersList = error;
      });
    }
    return this.usersList;
  };

  /**
   * delete dedicated number
   * @returns {Promise<[]>}
   * @param {string} params
   */
  deleteDedicatedNumber = async id => {
    const deleteResponse = await deleteDedicatedNumber(id);
    try {
      runInAction(() => {
        this.dedicatedNumResponse = deleteResponse;
        this.getAllDedicatedNo();
      });
    } catch (error) {
      runInAction(() => {
        this.dedicatedNumResponse = error;
      });
    }
    return this.dedicatedNumResponse;
  };

  /**
   * add single dedicated number
   * @returns {Promise<[]>}
   * @param {string} params
   */
  addDedicatedNumber = async values => {
    const response = await addDedicatedNumber(values);
    try {
      runInAction(() => {
        this.dedicatedNumResponse = response;
        this.getAllDedicatedNo();
      });
    } catch (error) {
      runInAction(() => {
        this.dedicatedNumResponse = error;
      });
    }
    return this.dedicatedNumResponse;
  };

  /**
   * add bulk dedicated number
   * @returns {Promise<[]>}
   * @param {file} params
   */
  addBulkDedicatedNum = async data => {
    const response = await addBulkDedicatedNums(data);
    try {
      runInAction(() => {
        this.dedicatedNumResponse = response;
        this.getAllDedicatedNo();
      });
    } catch (error) {
      runInAction(() => {
        this.dedicatedNumResponse = error;
      });
    }
    return this.dedicatedNumResponse;
  };

  /**
   * edit dedicated number
   * @returns {Promise<[]>}
   * @param {string} params
   */
  editDedicatedNumber = async (id, values) => {
    const response = await editDedicatedNumber(id, values);
    try {
      runInAction(() => {
        this.dedicatedNumResponse = response;
        this.getAllDedicatedNo();
      });
    } catch (error) {
      runInAction(() => {
        this.dedicatedNumResponse = error;
      });
    }
    return this.dedicatedNumResponse;
  };

  /**
   * active/InActive Dedicated number
   * @returns {Promise<[]>}
   * @param {string} params
   */
  activateDedicatedNum = async id => {
    const response = await activateDedicatedNum(id);
    try {
      runInAction(() => {
        this.dedicatedNumResponse = response;
        this.getAllDedicatedNo();
      });
    } catch (error) {
      runInAction(() => {
        this.dedicatedNumResponse = error;
      });
    }
    return this.dedicatedNumResponse;
  };

  dedicatedNumHistory = async id => {
    const params = {
      sortField: this.sortField,
      sortOrder: this.sortOrder
    };
    params.limit = 50;
    const usersResponse = await dedicatedNumHistory(id, params);
    try {
      runInAction(() => {
        const { items } = usersResponse;
        this.numHistory = items;
      });
    } catch (error) {
      runInAction(() => {
        this.numHistory = error;
      });
    }
    return this.numHistory;
  };

  // get dedicated number price by product_id
  getNumberPrice = async id => {
    const response = await getNumberPrice(id);
    try {
      runInAction(() => {
        this.dedicatedNumResponse = response;
      });
    } catch (error) {
      runInAction(() => {
        this.dedicatedNumResponse = error;
      });
    }
    return this.dedicatedNumResponse;
  };

  // release dedication no of user
  releaseDedicationNumber = async id => {
    const response = await releaseDedicationNumber(id);
    try {
      runInAction(() => {
        this.dedicatedNumResponse = response;
        this.getAllDedicatedNo();
      });
    } catch (error) {
      runInAction(() => {
        this.dedicatedNumResponse = error;
      });
    }
    return this.dedicatedNumResponse;
  };

  //*********** For User Verification Page *************//

  // get all submitted verification forms list
  getAllformsList = async () => {
    const params = {
      page: this.current,
      sortField: this.sortField,
      sortOrder: this.sortOrder,
      limit: this.pageSize
    };
    if (this.search) params.search = this.search;
    const response = await getVerificationForms(params);
    try {
      runInAction(() => {
        const { items, total, current, limit: pageSize } = response;
        this.verificationFormList = items;
        this.pageSize = pageSize;
        this.current = current;
        this.total = total;
      });
    } catch (error) {
      runInAction(() => {
        this.verificationFormList = error;
      });
    }
    return this.verificationFormList;
  };

  // admin action on verification form (approve or reject)
  verificationFormAction = async data => {
    const response = await verificationFormAction(data);
    try {
      runInAction(() => {
        this.verificationResponse = response;
        this.getAllformsList();
      });
    } catch (error) {
      runInAction(() => {
        this.verificationResponse = error;
      });
    }
    return this.verificationResponse;
  };

  // provider routes setting
  /**
   * @returns {Promise<[]>}
   * @param {string} params
   */
  getProviderRoutes = async (args = {}) => {
    const params = {
      page: this.current,
      sortField: this.sortField,
      sortOrder: this.sortOrder,
      limit: this.pageSize,
      ...args
    };
    if (this.search) params.search = this.search;
    const usersResponse = await getProviderRoutes(params);
    try {
      runInAction(() => {
        const { items, total, current, limit: pageSize } = usersResponse;
        this.usersList = items;
        this.pageSize = pageSize;
        this.current = current;
        this.total = total;
      });
    } catch (error) {
      runInAction(() => {
        this.usersList = error;
      });
    }
    return this.usersList;
  };

  deleteProviderRoutes = async (args = {}, id) => {
    const response = await deleteProviderRoutes(args, id);
    try {
      runInAction(() => {
        this.providerRouteResponse = response;
      });
    } catch (error) {
      runInAction(() => {
        this.providerRouteResponse = error;
      });
    }
    return this.providerRouteResponse;
  };

  mapProviderRoute = async values => {
    const response = await mapProviderRoute(values);
    try {
      runInAction(() => {
        this.providerRouteResponse = response;
      });
    } catch (error) {
      runInAction(() => {
        this.providerRouteResponse = error;
      });
    }
    return this.providerRouteResponse;
  };

  updateProviderMapping = async (values, id) => {
    const response = await updateProviderMapping(id, values);
    try {
      runInAction(() => {
        this.providerRouteResponse = response;
      });
    } catch (error) {
      runInAction(() => {
        this.providerRouteResponse = error;
      });
    }
    return this.providerRouteResponse;
  };
  // provider
  /**
   * @returns {Promise<[]>}
   * @param {string} params
   */
  getProviderSQS = async (args = {}) => {
    const params = {
      // page: this.current,
      // sortField: this.sortField,
      // sortOrder: this.sortOrder,
      // limit: this.pageSize,
      ...args
    };
    if (this.search) params.search = this.search;
    const usersResponse = await getProviderSQS(params);
    try {
      runInAction(() => {
        // const { items, total, current, limit: pageSize } = usersResponse;
        // this.usersList = items;
        // this.pageSize = pageSize;
        // this.current = current;
        // this.total = total;
        this.providerSQSList = usersResponse;
      });
    } catch (error) {
      runInAction(() => {
        this.providerSQSList = error;
      });
    }
    return this.providerSQSList;
  };

  deleteProviderSQS = async (args = {}, id) => {
    const response = await deleteProviderSQS(args, id);
    try {
      runInAction(() => {
        this.providerRouteResponse = response;
      });
    } catch (error) {
      runInAction(() => {
        this.providerRouteResponse = error;
      });
    }
    return this.providerRouteResponse;
  };

  mapProviderSQS = async values => {
    const response = await mapProviderSQS(values);
    try {
      runInAction(() => {
        this.providerRouteResponse = response;
      });
    } catch (error) {
      runInAction(() => {
        this.providerRouteResponse = error;
      });
    }
    return this.providerRouteResponse;
  };

  updateProviderSQS = async (values, id) => {
    const response = await updateProviderSQS(id, values);
    try {
      runInAction(() => {
        this.providerRouteResponse = response;
      });
    } catch (error) {
      runInAction(() => {
        this.providerRouteResponse = error;
      });
    }
    return this.providerRouteResponse;
  };

  // get providers list
  getAllProviders = async (args = {}) => {
    const response = await getAllProviders(args);
    try {
      runInAction(() => {
        this.providerList = response;
      });
    } catch (error) {
      runInAction(() => {
        this.providerList = error;
      });
    }
    return this.providerList;
  };

  // get default provider
  getDefaultProvider = async (args = {}) => {
    const response = await getDefaultProvider(args);
    try {
      runInAction(() => {
        this.defaultProvider = response?.data || {};
      });
    } catch (error) {
      runInAction(() => {
        this.defaultProvider = undefined;
      });
    }
    return this.defaultProvider;
  };

  // set default provider
  setDefaultProvider = async (args = {}) => {
    const response = await setDefaultProvider(args);
    try {
      runInAction(() => {
        this.providerRouteResponse = response;
      });
    } catch (error) {
      runInAction(() => {
        this.providerRouteResponse = error;
      });
    }
    return this.providerRouteResponse;
  };

  getPaymentMethodDisplay = type => {
    const types = {
      stripe: "Stripe",
      admin: "Admin"
    };
    return types[type] || type;
  };

  userUsageList = [];
  usagePagination = {
    current: 1,
    total: 0,
    pageSize: 10,
    totalPages: 1
  };
  loading = false;

  // Get user usage data
  getUserUsageData = async userId => {
    try {
      this.loading = true;

      const params = {
        page: this.usagePagination.current,
        limit: this.usagePagination.pageSize
      };

      const response = await getUserInvoices(userId, params);

      runInAction(() => {
        if (response.status && response.data) {
          this.userUsageList = response.data.items || [];
          this.usagePagination = {
            current: response.data.current || 1,
            total: response.data.total || 0,
            pageSize: response.data.limit || 10,
            totalPages: response.data.totalPages || 1
          };
        }
      });
    } catch (error) {
      console.error("Error fetching user usage data:", error);
      runInAction(() => {
        this.userUsageList = [];
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  // Set current page
  setUsagePage = page => {
    this.usagePagination.current = page;
    this.getUserUsageData();
  };

  // Set page size
  setUsagePageSize = pageSize => {
    this.usagePagination.pageSize = pageSize;
    this.usagePagination.current = 1;
    this.getUserUsageData();
  };
}

decorate(ObservableAdminGatewayStore, {
  adminDashboardGatewaySMSList: observable,
  adminDashboardGatewayMMSList: observable,
  adminDashboardSmsStats: observable,
  adminDashboardMmsStats: observable,
  adminBulkListForApproval: observable,
  bulkApprovalResponse: observable,
  selectedBulk: observable,
  usersList: observable,
  userDeleteResponse: observable,
  dedicatedNumResponse: observable,
  selectedDedicatedNum: observable,
  activeNum: observable,
  numStatus: observable,
  numType: observable,
  pageSize: observable,
  current: observable,
  total: observable,
  sortField: observable,
  sortOrder: observable,
  search: observable,
  type: observable,

  providerSQSList: observable,
  providerList: observable,

  verificationFormList: observable,

  totalPurchaseData: observable,
  userTotalPurchaseData: observable,
  totalUsageData: observable,
  totalUserExpenseData: observable,
  selectedUserId: observable,

  userPurchaseOrdersList: observable,
  userPurchaseOrdersResponse: observable,
  purchaseOrdersDateRange: observable,
  userEmailFilter: observable,
  mediumFilter: observable,

  purchaseOrdersPagination: observable,

  userSenderIdsUsageList: observable,
  userSenderIdsList: observable,
  userSenderIdsResponse: observable,
  userSenderIdsFilters: observable,

  senderIdsPagination: observable,

  userUsageList: observable,
  usagePagination: observable,
  loading: observable,

  getUserUsageData: action,
  setUsagePage: action,
  setUsagePageSize: action,

  getUserPurchaseOrders: action,
  setPurchaseOrdersDateRange: action,
  setUserEmailFilter: action,
  setMediumFilter: action,

  setUserSenderIdsFilters: action,
  clearUserSenderIdsFilters: action,
  setSenderIdsPage: action,
  getUserSenderIds: action
});
export default new ObservableAdminGatewayStore();
